import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * "Why isn't this selling?" - the AI listing review.
 *
 * Free sellers see the verdict and their single worst problem. That is
 * deliberately useful on its own: a tool that only teases is ignored, whereas
 * one that fixes a real problem earns the subscription. Pro unlocks every
 * finding plus the sold-comparable numbers behind them.
 */
interface Finding {
  severity: 'critical' | 'warning' | 'good';
  area: 'price' | 'photos' | 'title' | 'description' | 'demand' | 'timing';
  headline: string;
  detail: string;
  action?: string;
}
interface Review {
  listingId: string;
  title: string;
  score: number;
  verdict: string;
  findings: Finding[];
  hiddenFindings?: number;
  pro: boolean;
  comparables: {
    soldCount: number;
    medianSoldGBP: number | null;
    medianDaysToSell: number | null;
    yourPriceGBP: number;
  };
}

const AREA_ICON: Record<Finding['area'], string> = {
  price: '£', photos: '📷', title: 'T', description: '¶', demand: '📈', timing: '⏱',
};
const SEV: Record<Finding['severity'], { ring: string; chip: string; label: string }> = {
  critical: { ring: 'border-bad/30 bg-bad/5',   chip: 'bg-bad/10 text-bad',           label: 'FIX THIS' },
  warning:  { ring: 'border-warn/30 bg-warn/5', chip: 'bg-warn/15 text-warn-dark',    label: 'COULD IMPROVE' },
  good:     { ring: 'border-ok/25 bg-ok/5',     chip: 'bg-ok/15 text-ok-dark',        label: 'GOOD' },
};

export function ListingReview({
  listings,
}: {
  listings: { id: string; title: string; views: number; saves: number }[];
}) {
  // Default to the listing most likely to have a problem: most views, fewest
  // saves. That is where the review has something worth saying.
  const worstFirst = [...listings].sort((a, b) => (b.views - b.saves * 5) - (a.views - a.saves * 5));
  const [selected, setSelected] = useState<string>(worstFirst[0]?.id ?? '');

  const { data: review, isFetching, isError } = useQuery<Review>({
    queryKey: ['listing-review', selected],
    queryFn: async () => (await api.get(`/promotions/review/${selected}`)).data,
    enabled: !!selected,
  });

  if (!listings.length) {
    return (
      <div className="card-flat p-6">
        <div className="stamp text-[9px] text-mute">LISTING REVIEW</div>
        <p className="text-[14px] text-mute mt-2">List an item and Souqli will tell you why it is or isn’t selling.</p>
      </div>
    );
  }

  const scoreColour = !review ? 'text-mute'
    : review.score >= 80 ? 'text-ok-dark'
    : review.score >= 55 ? 'text-warn-dark'
    : 'text-bad';

  return (
    <div className="card-flat overflow-hidden">
      <div className="p-5 border-b border-warmline">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="stamp text-[9px] text-gold-dark">LISTING REVIEW</div>
            <h2 className="display text-2xl mt-1">Why isn’t this selling?</h2>
            <p className="text-[13px] text-mute mt-1 max-w-[52ch]">
              Compares your listing against items that actually sold in the same category - not generic advice.
            </p>
          </div>
          <span className="pill pill-ok text-[9px] shrink-0">FREE</span>
        </div>

        <select
          className="input mt-4 w-full"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
        >
          {worstFirst.map((l) => (
            <option key={l.id} value={l.id}>
              {l.title} · {l.views} views, {l.saves} saves
            </option>
          ))}
        </select>
      </div>

      {isFetching && <div className="p-6 text-[14px] text-mute">Comparing against recent sales…</div>}

      {isError && (
        <div className="p-6 text-[14px] text-mute">
          Couldn’t review that listing just now. Try another, or refresh the page.
        </div>
      )}

      {review && !isFetching && (
        <>
          <div className="p-5 flex items-center gap-5 bg-cream-deep/40">
            <div className="text-center shrink-0">
              <div className={`display text-4xl leading-none ${scoreColour}`}>{review.score}</div>
              <div className="stamp text-[8px] text-mute mt-1">OUT OF 100</div>
            </div>
            <p className="text-[14px] leading-relaxed">{review.verdict}</p>
          </div>

          <div className="p-5 space-y-3">
            {review.findings.map((f, i) => (
              <div key={i} className={`rounded-luxe border p-4 ${SEV[f.severity].ring}`}>
                <div className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-white border border-warmline grid place-items-center text-[12px] shrink-0">
                    {AREA_ICON[f.area]}
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`pill text-[8px] ${SEV[f.severity].chip}`}>{SEV[f.severity].label}</span>
                      <span className="stamp text-[8px] text-mute">{f.area.toUpperCase()}</span>
                    </div>
                    <h3 className="text-[15px] font-medium mt-1.5">{f.headline}</h3>
                    <p className="text-[13px] text-mute mt-1 leading-relaxed">{f.detail}</p>
                    {f.action && (
                      <p className="text-[13px] mt-2 leading-relaxed">
                        <span className="stamp text-[8px] text-gold-dark mr-1.5">DO THIS</span>
                        {f.action}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Pro: the numbers behind the verdict. */}
            {review.pro && review.comparables.medianSoldGBP != null && (
              <div className="section-dark rounded-luxe bg-ink text-cream p-4">
                <div className="stamp text-[8px] text-gold">THE COMPARABLES</div>
                <div className="grid grid-cols-3 gap-4 mt-2.5">
                  <div>
                    <div className="display text-xl">£{review.comparables.medianSoldGBP.toFixed(2)}</div>
                    <div className="text-[11px] text-cream/60 mt-0.5">median sold price</div>
                  </div>
                  <div>
                    <div className="display text-xl">
                      {review.comparables.medianDaysToSell != null ? `${Math.round(review.comparables.medianDaysToSell)}d` : '-'}
                    </div>
                    <div className="text-[11px] text-cream/60 mt-0.5">typical time to sell</div>
                  </div>
                  <div>
                    <div className="display text-xl">{review.comparables.soldCount}</div>
                    <div className="text-[11px] text-cream/60 mt-0.5">items compared</div>
                  </div>
                </div>
              </div>
            )}

            {!review.pro && !!review.hiddenFindings && (
              <div className="rounded-luxe border border-gold/30 bg-gold/5 p-4 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-[14px]">
                    <span className="font-medium">{review.hiddenFindings} more finding{review.hiddenFindings === 1 ? '' : 's'}</span>
                    {' '}on this listing, plus the sold prices behind the verdict.
                  </p>
                  <p className="text-[12px] text-mute mt-0.5">
                    Souqli Pro reviews every listing in full.
                  </p>
                </div>
                <a href="#pro" className="btn btn-gold btn-sm shrink-0">See Pro</a>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
